import InputPassword from "components/ui/InputPassword"
import InputPhone from "components/ui/InputPhone"
import { apiFirebase } from "api"
import LoginGoogleAndFacebook from "./LoginGoogleAndFacebook"
import { useEffect, useRef, useState } from "react"
import { Modal, notification } from "components/ui"
import Register from "./Register"
import UpdatePhone from "./UpdatePhone"
import QRCode from "./QRcode"
import { initializeApp } from "firebase/app"
import { getMessaging, getToken } from "firebase/messaging"
import Link from "next/link"
import { getAuthError, getAuthHeaders, kickoutDevice, loginWithPassword, loginWithProvider } from "./authService"
import useCompleteLogin from "./useCompleteLogin"
import { clearOidcTransaction, handleLogin, OIDC_CALLBACK_PAYLOAD_KEY } from "utils/base"

const initParams = { phone: "", password: "" }

export default function LoginForm({ initView = "login", onClose }) {
  const [view, setView] = useState(initView)
  const [pendingLogin, setPendingLogin] = useState(null)
  const [params, setParams] = useState(initParams)
  const [errors, setErrors] = useState(initParams)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResolvingDevice, setIsResolvingDevice] = useState(false)
  const __forgotPassword = useRef(false)
  const __tokenFirebase = useRef()
  const __userId = useRef()
  const __isSubmittingOidc = useRef(false)
  const __processedOidcStates = useRef({})
  const initHeader = useRef(getAuthHeaders())
  const completeLogin = useCompleteLogin({ initView, onClose })

  function clearPendingLogin() {
    if (pendingLogin?.type === "oidc") {
      clearOidcTransaction(pendingLogin.state)
    }
    setPendingLogin(null)
  }

  function onDeviceLimit(loginAttempt) {
    setPendingLogin(loginAttempt)
  }

  async function onLogin(authData, method) {
    const result = await completeLogin(authData, method)
    if (result.requiresPhoneUpdate) setView("updatephone")
  }

  async function onSubmit() {
    if (isSubmitting) return
    const { phone, password } = params
    let _error = {}

    if (!phone) _error.phone = "Số điện thoại không được để trống"
    if (Number(phone) && phone.length < 9) _error.phone = "Phone gồm 10 số bắt đầu từ số 0"
    if (password.length < 6) {
      _error.password = "Mật khẩu lớn hơn 6 ký tự"
    }

    if (Object.keys(_error).length > 0) {
      setErrors(_error)
      return
    } else {
      setErrors(initParams)
    }

    setIsSubmitting(true)
    try {
      const response = await loginWithPassword({ phone, password })
      if (!response?.success || !response?.data) throw new Error("Dữ liệu đăng nhập không hợp lệ")

      __userId.current = response.data.user_id
      sendTokenFirebase()
      await onLogin(response.data, "password")
    } catch (error) {
      const authError = getAuthError(error)
      if (authError.errorCode === 411) {
        setPendingLogin({
          type: "password",
          uuid: authError.data?.uuid,
          message: authError.message,
          userId: authError.data?.user_id,
          phone,
          password
        })
      } else {
        notification.error({ message: authError.message })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function onKickDevice() {
    if (!pendingLogin || isResolvingDevice) return
    setIsResolvingDevice(true)
    try {
      const kickoutResponse = await kickoutDevice(pendingLogin.uuid)
      if (kickoutResponse?.error_code !== 200) {
        throw new Error(kickoutResponse?.error_message || "Không thể đăng xuất thiết bị cũ")
      }

      if (pendingLogin.type === "oidc") {
        clearOidcTransaction(pendingLogin.state)
        setPendingLogin(null)
        handleLogin({ redirectUrl: process.env.NEXT_PUBLIC_REDIRECT_URL })
        return
      }

      let response
      let method
      if (pendingLogin.type === "password") {
        response = await loginWithPassword({ phone: pendingLogin.phone, password: pendingLogin.password })
        method = "password"
      } else {
        response = await loginWithProvider({
          providerName: pendingLogin.providerName,
          providerAccessToken: pendingLogin.providerAccessToken
        })
        method = pendingLogin.providerName
      }

      if (!response?.data) throw new Error("Dữ liệu đăng nhập không hợp lệ")

      __userId.current = response.data.user_id
      if (pendingLogin.type === "password") sendTokenFirebase()
      await onLogin(response.data, method)

      setPendingLogin(null)
    } catch (error) {
      const authError = getAuthError(error)
      notification.error({ message: authError.message })
    } finally {
      setIsResolvingDevice(false)
    }
  }

  function onChangeData(k, value) {
    const __value = value[value.length - 1]
    if (view == "register" && k === "password" && __value === " ") {
      notification.error({
        message: "Mật khẩu không được chứa dấu cách"
      })
      return
    }
    if (k === "phone" && value.length > 0) {
      if (value.length === 1 && value[0] !== "0" && params?.phone?.length !== 2) {
        notification.error({
          message: "Số điện thoại bắt đầu từ số 0"
        })
        setErrors((state) => ({ ...state, phone: "Số điện thoại bắt đầu từ số 0" }))
        return
      }
    }
    setErrors({})
    setParams((s) => ({ ...s, [k]: value }))
  }

  function onChangePassword() {
    __forgotPassword.current = true
    setView("register")
  }

  function onChangeView(value) {
    __forgotPassword.current = false
    setView(value)
    setParams(initParams)
  }

  async function handleOidcAuthCode(payload) {
    const { code, state } = payload || {}
    if (!code || !state || __isSubmittingOidc.current || __processedOidcStates.current[state]) {
      return
    }

    __isSubmittingOidc.current = true
    __processedOidcStates.current[state] = true
    try {
      const response = await loginWithProvider({
        providerName: "onlive",
        providerAccessToken: code
      })

      if (!response?.data) throw new Error("Invalid login data")

      __userId.current = response.data.user_id
      await onLogin(response.data, "onlive")
      clearOidcTransaction(state)
    } catch (error) {
      const authError = getAuthError(error)
      if (authError.errorCode === 411) {
        setPendingLogin({
          type: "oidc",
          uuid: authError.data?.uuid,
          message: authError.message,
          userId: authError.data?.user_id,
          state
        })
      } else {
        clearOidcTransaction(state)
        notification.error({ message: authError.message })
      }
    } finally {
      __isSubmittingOidc.current = false
    }
  }

  useEffect(() => {
    function onOidcMessage(event) {
      if (event.origin !== window.location.origin || event.data?.type !== "OIDC_AUTH_CODE_RECEIVED") {
        return
      }

      handleOidcAuthCode(event.data.payload)
    }

    consumeOidcPayloadFromStorage()

    const channel = new BroadcastChannel("auth_channel")

    channel.onmessage = (event) => {
      if (event.data?.type === "OIDC_AUTH_CODE_RECEIVED") {
        handleOidcAuthCode(event.data.payload)
      }
    }

    window.addEventListener("message", onOidcMessage)

    return () => {
      window.removeEventListener("message", onOidcMessage)
      channel.close()
    }
  }, [])

  async function sendTokenFirebase() {
    let body = {
      registration_token: __tokenFirebase.current,
      platform: "web",
      user_id: __userId.current
    }

    try {
      await apiFirebase.createTokenFirebase(body)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    // Initialize Firebase
    const firebaseApp = initializeApp({
      apiKey: "AIzaSyChP2yOm3j5hQHa-YdLuqn8XhkAYHQLdwI",
      authDomain: "onplus-5a601.firebaseapp.com",
      projectId: "onplus-5a601",
      storageBucket: "onplus-5a601.appspot.com",
      messagingSenderId: "190122352939",
      appId: "1:190122352939:web:6d086f9f254fc640b6256e",
      measurementId: "G-8CTEDKZ2SV"
    })

    let messaging
    try {
      messaging = getMessaging(firebaseApp)

      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("Notification permission granted.")

          // Get the token
          getToken(messaging, {
            vapidKey: "BEqT0Ao_48Th8rxkgEiFkK0Hrra7OwSxaYOu8-g9NL2tGLql-KrgRjD_hdDkqw76Ykslcgw1pAvmot56hjbVRy8"
          })
            .then((currentToken) => {
              if (currentToken) {
                __tokenFirebase.current = currentToken
              } else {
                console.log("No registration token available. Request permission to generate one.")
              }
            })
            .catch((err) => {
              console.log("An error occurred while retrieving token. ", err)
            })
        } else {
          console.log("Unable to get permission to notify.")
        }
      })
    } catch (err) {
      console.error("Failed to initialize Firebase Messaging", err)
    }
  }, [])

  function consumeOidcPayloadFromStorage() {
    const rawPayload = sessionStorage.getItem(OIDC_CALLBACK_PAYLOAD_KEY)
    if (!rawPayload) return

    try {
      const payload = JSON.parse(rawPayload)
      sessionStorage.removeItem(OIDC_CALLBACK_PAYLOAD_KEY)
      handleOidcAuthCode(payload)
    } catch (error) {
      console.error("Invalid OIDC callback payload:", error)
      sessionStorage.removeItem(OIDC_CALLBACK_PAYLOAD_KEY)
    }
  }

  const { password, phone } = params

  return (
    <div className="flex justify-between flex-col modal_login bg-dark-700 text-dark-900 rounded">
      <div className="p-4 flex-grow flex-col flex justify-center sm:p-8 md:p-10">
        {view === "login" && (
          <div>
            <h1 className="md:text-3xl text-center font-bold text-dark-100 mb-6 text-xl">ĐĂNG NHẬP</h1>
            <InputPhone
              onChange={(value) => onChangeData("phone", value?.trim())}
              placeholder={"Nhập số điện thoại của bạn"}
              error={errors.phone}
              name="phone"
              value={phone}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit()
              }}
            />
            <InputPassword
              onChange={(e) => onChangeData("password", e.target.value)}
              placeholder="Nhập mật khẩu"
              name="password"
              error={errors.password}
              value={password}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit()
              }}
            />
            <button
              disabled={isSubmitting}
              onClick={onSubmit}
              className="m-auto rounded py-2 px-8 md:text-lg text-base flex items-center mb-4 space-x-4 hover:bg-primary-hover text-dark bg-primary-500 font-semibold"
            >
              <span>{isSubmitting ? "ĐANG ĐĂNG NHẬP..." : "ĐĂNG NHẬP"}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
            <div className="text-center text-sm md:text-base">
              <button className="text-dark-100 underline" onClick={onChangePassword}>
                Bạn quên mật khẩu?
              </button>
            </div>
          </div>
        )}
        {view === "qr" && <QRCode initHeader={initHeader.current} onChangeView={onChangeView} onLogin={onLogin} />}

        {view === "register" && <Register setView={setView} isResetPassword={__forgotPassword.current} />}
        {view === "updatephone" && (
          <UpdatePhone onLogin={onLogin} setView={setView} isResetPassword={__forgotPassword.current} />
        )}
        {view === "login" && (
          <div>
            <div className="text-dark-300 text-center mt-8 text-sm">hoặc đăng nhập bằng</div>
            <LoginGoogleAndFacebook onChangeView={onChangeView} onLogin={onLogin} onDeviceLimit={onDeviceLimit} />
          </div>
        )}
        {view === "login" && (
          <p className="text-center text-xs min-[391px]:text-sm leading-[18px] sm:text-sm sm:leading-5 text-[#FAFAF9]">
            Bằng cách đăng nhập, bạn chấp nhận{" "}
            <Link href="/chinh-sach" className="inline text-[#1877F2] underline font-bold">
              Điều khoản sử dụng
            </Link>
            {", "}
            <Link href="/chinh-sach" className="inline text-[#1877F2] underline font-bold">
              Chính sách bảo mật
            </Link>
            {", "}
            <Link href="/chinh-sach-bao-ve-du-lieu-ca-nhan" className="inline text-[#1877F2] underline font-bold">
              Chính sách bảo vệ dữ liệu cá nhân
            </Link>{" "}
            và{" "}
            <Link href="/chinh-sach-bao-ve-thong-tin-khach-hang" className="inline text-[#1877F2] underline font-bold">
              Chính sách bảo vệ thông tin khách hàng
            </Link>{" "}
            của chúng tôi
          </p>
        )}
      </div>

      {view === "login" && (
        <div className="text-center text-dark-500 bg-dark-500 px-4 py-2 br-b-8">
          <div className="text-sm">Bạn chưa có tài khoản?</div>
          <button
            className={"text-dark-100 cursor-pointer font-bold text-base md:text-lg underline"}
            onClick={() => onChangeView("register")}
          >
            ĐĂNG KÝ
          </button>
        </div>
      )}

      {view === "register" && (
        <div className="text-center text-dark-500 bg-dark-500 py-2 px-4 br-b-8">
          <div className="text-sm mb-2 md:mb-0">Bạn đã có tài khoản?</div>
          <button
            className="text-dark-100 cursor-pointer font-semibold md:text-lg text-base underline"
            onClick={() => onChangeView("login")}
          >
            ĐĂNG NHẬP
          </button>
        </div>
      )}

      <Modal isOpen={Boolean(pendingLogin)} onCancel={clearPendingLogin}>
        <div className="max-w-[464px] p-8">
          <div className="text-2xl font-semibold text-center mb-2">Thông báo</div>
          <div className=" text-lg text-center ">
            <div className="text-dark-300">
              Tài khoản của bạn đã đăng nhập quá 3 thiết bị. Vui lòng chọn Tiếp tục để đăng xuất khỏi thiết bị đã đăng
              nhập cũ nhất.
            </div>
            <hr className="mt-6" style={{ borderTop: "solid 0.5px", opacity: "0.3" }} />
            <div className="mt-4">
              <button disabled={isResolvingDevice} className="px-2 mr-8 font-medium" onClick={clearPendingLogin}>
                Hủy
              </button>
              <button disabled={isResolvingDevice} className="px-2 text-primary-500 font-medium" onClick={onKickDevice}>
                {isResolvingDevice ? "Đang xử lý..." : "Tiếp tục"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
// curl --location -g '{{onsports_tracking_session}}/api/v1/tracking-session/' \
// --data '{
//     "user_id": 11234,
//     "event_name": "app_foreground",
//     "device_platform": 1
// }'
